<template>
  <div
    v-if="showRaidRecovery"
    class="rounded-lg border border-warning/40 bg-bg-surface px-4 py-3 text-sm text-text-primary shadow-sm"
    role="alert"
  >
    <p class="font-semibold">{{ messages.raidRecovery.title }}</p>
    <p class="mt-1 text-text-secondary">{{ messages.raidRecovery.body }}</p>
    <AppButton
      :variant="ButtonVariant.Secondary"
      :size="ButtonSize.Sm"
      class="mt-3"
      @click="focusCalendar"
    >{{ messages.raidRecovery.action }}</AppButton>
  </div>
</template>

<script setup lang="ts">
/**
 * Raid-recovery banner.
 *
 * Watches `route + store.error` from the planner store and surfaces a
 * copy-driven recovery strip on the Calendar surface when a `/:guildId/plan/:raidId`
 * navigation fails (raid deleted, member access revoked, etc.). The
 * banner clears `store.error` once it has rendered so the same error
 * doesn't surface on a subsequent navigation.
 *
 * Phase 8 moves this logic out of `App.vue` so the host shell stays
 * feature-agnostic. The banner is feature-local because it reads the
 * planner store — that's a feature reading its own store, allowed by
 * the architecture. `App.vue` mounts it without any props.
 */
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import { AppButton, ButtonSize, ButtonVariant } from '@/shared/ui'
import { APP_ROUTE_NAMES } from '@/shared/constants/routes'
import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { appShellEn } from './locales/en.ts'
import { appShellRu } from './locales/ru.ts'
import { useGvgPlannerStore } from '../../features/gvg-planner/stores/gvgPlannerStore'

const route = useRoute()
const store = useGvgPlannerStore()
const showRaidRecovery = ref<boolean>(false)

const catalog = defineLocaleCatalog({ en: appShellEn, ru: appShellRu })
const messages = useLocaleMessages(catalog)

function isUnavailableRaidError(error: string | null): boolean {
  if (error === null) return false
  return /not[_\s-]?found|selected raid is no longer available/i.test(error)
}

function focusCalendar(): void {
  document.getElementById('calendar-content')?.focus()
}

watch(
  () => [route.name, store.error] as const,
  ([routeName, error]) => {
    if (routeName !== APP_ROUTE_NAMES.Calendar) {
      showRaidRecovery.value = false
      return
    }
    if (isUnavailableRaidError(error)) {
      showRaidRecovery.value = true
      store.error = null
    }
  },
)
</script>
